import { useEffect, useRef } from 'react'
import {
  CheckCircleIcon,
  ArrowPathIcon,
  ClockIcon,
  SparklesIcon,
  CpuChipIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ChartBarIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  BookOpenIcon,
  BeakerIcon,
  BoltIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import type { ExecutionStep } from '@/hooks/useIntelligentExecutionWS'

// Specialist icons mapping
const specialistIcons: Record<string, React.ElementType> = {
  coding: CodeBracketIcon,
  documents: DocumentTextIcon,
  graphs: ChartBarIcon,
  spreadsheets: TableCellsIcon,
  presentations: PresentationChartBarIcon,
  data_analysis: BeakerIcon,
  literature_review: BookOpenIcon,
}

interface ExecutionTimelineProps {
  steps: ExecutionStep[]
  currentPhase: string
  autoScroll?: boolean
}

export default function ExecutionTimeline({ steps, currentPhase, autoScroll = true }: ExecutionTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new steps arrive
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [steps, autoScroll])

  const getStepIcon = (step: ExecutionStep) => {
    if (step.status === 'error') return ExclamationTriangleIcon
    if (step.status === 'revision') return ArrowPathIcon
    if (step.status === 'running') return ArrowPathIcon
    if (step.status === 'completed') return CheckCircleIcon

    // Type-specific icons
    if (step.type.includes('supervisor')) return SparklesIcon
    if (step.type.includes('parallel')) return BoltIcon
    if (step.type.includes('quality')) return MagnifyingGlassIcon
    if (step.specialist) return specialistIcons[step.specialist] || CpuChipIcon

    return ClockIcon
  }

  const getStepColors = (step: ExecutionStep) => {
    switch (step.status) {
      case 'running':
        return {
          icon: 'text-blue-400',
          line: 'bg-blue-400',
          bg: 'bg-blue-900/20',
          border: 'border-blue-700'
        }
      case 'completed':
        return {
          icon: 'text-green-400',
          line: 'bg-green-400',
          bg: 'bg-green-900/20',
          border: 'border-green-700'
        }
      case 'error':
        return {
          icon: 'text-red-400',
          line: 'bg-red-400',
          bg: 'bg-red-900/20',
          border: 'border-red-700'
        }
      case 'revision':
        return {
          icon: 'text-yellow-400',
          line: 'bg-yellow-400',
          bg: 'bg-yellow-900/20',
          border: 'border-yellow-700'
        }
      default:
        return {
          icon: 'text-gray-400',
          line: 'bg-gray-600',
          bg: 'bg-gray-800',
          border: 'border-gray-700'
        }
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  if (steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <ClockIcon className="w-12 h-12 mb-3" />
        <p className="text-sm">Waiting for execution to start...</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto max-h-[500px] pr-2 space-y-2"
    >
      {steps.map((step, index) => {
        const Icon = getStepIcon(step)
        const colors = getStepColors(step)
        const isLast = index === steps.length - 1

        return (
          <div key={step.id} className="relative flex">
            {/* Timeline line */}
            <div className="flex flex-col items-center mr-4">
              <div
                className={clsx(
                  'flex items-center justify-center w-8 h-8 rounded-full',
                  colors.bg,
                  'border',
                  colors.border
                )}
              >
                <Icon
                  className={clsx(
                    'w-4 h-4',
                    colors.icon,
                    step.status === 'running' && 'animate-spin'
                  )}
                />
              </div>
              {!isLast && (
                <div
                  className={clsx(
                    'w-0.5 flex-1 min-h-[24px]',
                    colors.line
                  )}
                />
              )}
            </div>

            {/* Content */}
            <div
              className={clsx(
                'flex-1 pb-4 min-w-0',
                isLast && 'pb-0'
              )}
            >
              <div
                className={clsx(
                  'rounded-lg p-3 border',
                  colors.bg,
                  colors.border
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white text-sm">
                      {step.title}
                    </span>
                    {step.isParallel && (
                      <span className="px-1.5 py-0.5 text-xs bg-purple-900/50 text-purple-300 rounded">
                        Parallel
                      </span>
                    )}
                    {step.qualityScore !== undefined && (
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 text-xs rounded',
                          step.qualityScore >= 8
                            ? 'bg-green-900/50 text-green-300'
                            : step.qualityScore >= 6
                            ? 'bg-yellow-900/50 text-yellow-300'
                            : 'bg-red-900/50 text-red-300'
                        )}
                      >
                        {step.qualityScore.toFixed(1)}/10
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatTime(step.timestamp)}
                  </span>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-400 break-words">
                  {step.description}
                </p>

                {/* Metadata */}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  {step.specialist && (
                    <span className="flex items-center">
                      <CpuChipIcon className="w-3 h-3 mr-1" />
                      {step.specialist}
                    </span>
                  )}
                  {step.duration && (
                    <span className="flex items-center">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      {formatDuration(step.duration)}
                    </span>
                  )}
                  {step.parallelGroup !== undefined && (
                    <span className="flex items-center">
                      <BoltIcon className="w-3 h-3 mr-1" />
                      Group {step.parallelGroup}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}

      {/* Current phase indicator */}
      {currentPhase !== 'completed' && currentPhase !== 'error' && currentPhase !== 'idle' && (
        <div className="flex items-center text-blue-400 text-sm pl-12 pt-2">
          <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
          <span className="capitalize">{currentPhase.replace('_', ' ')}...</span>
        </div>
      )}
    </div>
  )
}
